<template>
  <div class="oh-page" v-loading="loading">

    <!-- Header -->
    <div class="oh-header">
      <div class="oh-inner oh-header-inner">
        <h1 class="oh-title">Order History</h1>
        <div class="oh-header-actions">
          <button class="oh-btn oh-btn--ghost" @click="router.push('/request')">+ New Order</button>
          <button class="oh-btn oh-btn--csv" @click="downloadCSV"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><line x1="10" y1="9" x2="14" y2="9"/></svg> CSV</button>
        </div>
      </div>
    </div>

    <!-- Filters + summary (re-added from main) -->
    <div class="oh-inner oh-toolbar">
      <div class="oh-filter">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          format="DD/MM/YYYY"
          value-format="YYYY-MM-DD"
          size="default"
          clearable
          @change="handleDateRangeChange"
        />
        <button class="oh-btn oh-btn--navy" :disabled="loading" @click="applyFilters">Apply</button>
        <button class="oh-btn oh-btn--ghost" :disabled="loading" @click="clearFilters">Clear</button>
      </div>
    </div>

    <div class="oh-inner oh-list-wrap">

      <!-- Table -->
      <div class="oh-table">

        <!-- Column headers -->
        <div class="oh-thead">
          <div class="oh-th oh-th--order">ORDER</div>
          <div class="oh-th oh-th--status">STATUS</div>
          <div class="oh-th oh-th--user">USER</div>
          <div class="oh-th oh-th--date">DATE</div>
          <div class="oh-th oh-th--amount">AMOUNT</div>
          <div class="oh-th oh-th--from">FROM</div>
          <div class="oh-th oh-th--to">TO</div>
          <div class="oh-th oh-th--action"></div>
        </div>

        <!-- Rows -->
        <div class="oh-tbody">
          <div
            v-for="(row, i) in paginatedData"
            :key="row.id"
            class="oh-row-wrap"
            :class="{ 'oh-row-wrap--open': expandedRows.has(row.id) }"
          >
            <div class="oh-row" @click="toggleRow(row)">
              <div class="oh-td oh-td--order">
                <span class="oh-ref">{{ row.order_number }}</span>
              </div>
              <div class="oh-td oh-td--status">
                <span class="oh-badge" :class="getStatusClass(row.order_status)">{{ row.order_status_text }}</span>
              </div>
              <div class="oh-td oh-td--user">
                <span class="oh-td-text">{{ row.user_name }}</span>
              </div>
              <div class="oh-td oh-td--date">
                <span class="oh-td-muted">{{ transformDateSQL(row.scheduled_time) }}</span>
              </div>
              <div class="oh-td oh-td--amount">
                <span class="oh-td-amount">KES {{ row.cost.toLocaleString() }}</span>
              </div>
              <div class="oh-td oh-td--from">
                <span class="oh-td-muted">{{ row.pickup }}</span>
              </div>
              <div class="oh-td oh-td--to">
                <span class="oh-td-muted">{{ row.dropoff }}</span>
              </div>
              <div class="oh-td oh-td--action">
                <span class="oh-view-link">
                  {{ expandedRows.has(row.id) ? 'Hide' : 'View' }}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </span>
              </div>
            </div>

            <!-- Expanded detail -->
            <Transition name="expand">
              <div class="oh-detail" v-if="expandedRows.has(row.id)" v-loading="row.orderDetails == undefined">
                <div class="oh-detail-grid" v-if="row.orderDetails != undefined">

                  <!-- Order info card -->
                  <div class="oh-dcard">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--blue">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></svg>
                      </div>
                      <div class="oh-dcard-label">Order Info</div>
                    </div>
                    <div class="oh-dkv-list">
                      <div class="oh-dkv"><span class="oh-dk">Amount</span><span class="oh-dv oh-dv--navy">KES {{ row.cost.toLocaleString() }}</span></div>
                      <div class="oh-dkv"><span class="oh-dk">Distance</span><span class="oh-dv">{{ row.orderDetails.order_details.distance_value }} km</span></div>
                      <div class="oh-dkv"><span class="oh-dk">Requested by</span><span class="oh-dv">{{ row.orderDetails.customer_details.user_name }}</span></div>
                      <div class="oh-dkv" v-if="row.orderDetails.delivery_logs.find((log:any) => log.log_type === 'Picked')">
                        <span class="oh-dk">Picked up</span>
                        <span class="oh-dv">{{ transformDate(row.orderDetails.delivery_logs.find((log:any) => log.log_type === 'Picked')['log_time']) }}</span>
                      </div>
                      <div class="oh-dkv" v-if="row.orderDetails.delivery_logs.find((log:any) => log.log_type === 'Dropped')">
                        <span class="oh-dk">Delivered</span>
                        <span class="oh-dv oh-dv--green">{{ transformDate(row.orderDetails.delivery_logs.find((log:any) => log.log_type === 'Dropped')['log_time']) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Route card -->
                  <div class="oh-dcard">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--purple">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 6-9 13-9 13S3 16 3 10a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      </div>
                      <div class="oh-dcard-label">Route</div>
                    </div>
                    <div class="oh-route">
                      <!-- Pickup -->
                      <div class="oh-stop">
                        <div class="oh-stop-track">
                          <div class="oh-stop-dot oh-stop-dot--green"></div>
                          <div class="oh-stop-line"></div>
                        </div>
                        <div class="oh-stop-text">
                          <div class="oh-stop-type">Pick-up</div>
                          <div class="oh-stop-name">{{ row.orderDetails.paths[0]?.name || row.orderDetails.order_details.from_name }}</div>
                        </div>
                      </div>
                      <!-- Each dropoff stop -->
                      <div
                        v-for="(stop, si) in row.orderDetails.paths.slice(1)"
                        :key="si"
                        class="oh-stop"
                      >
                        <div class="oh-stop-track">
                          <div class="oh-stop-dot" :class="si === row.orderDetails.paths.length - 2 ? 'oh-stop-dot--amber' : 'oh-stop-dot--mid'"></div>
                          <div class="oh-stop-line" v-if="si < row.orderDetails.paths.length - 2"></div>
                        </div>
                        <div class="oh-stop-text">
                          <div class="oh-stop-type oh-stop-type--drop">
                            <span class="oh-stop-num">{{ si + 1 }}</span>
                            Drop-off
                          </div>
                          <div class="oh-stop-name">{{ stop.name }}</div>
                        </div>
                      </div>
                    </div>
                    <div class="oh-detail-actions">
                      <a v-if="row.order_status < 4" :href="returnOrderTrackingLink(row)" class="oh-track-link">Track this order →</a>
                    </div>

                    <!-- Inline delivery documents gallery -->
                    <div v-if="hasDeliveryDocuments(row.orderDetails.paths)" class="oh-docs-section">
                      <button
                        class="oh-docs-toggle"
                        @click.stop="toggleDocs(row.id)"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                        Delivery Documents
                        <span class="oh-docs-count">{{ extractDocumentsInfo(row.orderDetails.paths).length }}</span>
                        <svg class="oh-docs-chevron" :class="{ 'oh-docs-chevron--open': openDocs.has(row.id) }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                      </button>

                      <Transition name="docs-expand">
                        <div v-if="openDocs.has(row.id)" class="oh-docs-gallery">
                          <div
                            v-for="(doc, di) in extractDocumentsInfo(row.orderDetails.paths)"
                            :key="di"
                            class="oh-doc-thumb"
                            @click.stop="openDocModal(extractDocumentsInfo(row.orderDetails.paths), di, row)"
                          >
                            <div class="oh-doc-img-wrap">
                              <img v-if="isImage(doc.src)" :src="doc.src" :alt="doc.title" class="oh-doc-img" @error="(e: any) => e.target.style.display='none'" />
                              <div v-else class="oh-doc-file-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#475569" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                <span class="oh-doc-ext">{{ getExt(doc.src) }}</span>
                              </div>
                              <div class="oh-doc-overlay">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                              </div>
                            </div>
                            <div class="oh-doc-label">{{ doc.title || 'Document ' + (di + 1) }}</div>
                          </div>
                        </div>
                      </Transition>
                    </div>
                  </div>

                  <!-- Driver card -->
                  <div class="oh-dcard">
                    <div class="oh-dcard-head">
                      <div class="oh-dcard-icon oh-dcard-icon--teal">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                      </div>
                      <div class="oh-dcard-label">Driver</div>
                    </div>
                    <div class="oh-driver">
                      <div class="oh-driver-avatar">{{ row.orderDetails.driver_details.name?.charAt(0)?.toUpperCase() }}</div>
                      <div>
                        <div class="oh-driver-name">{{ row.orderDetails.driver_details.name }}</div>
                        <div class="oh-driver-plate">{{ row.orderDetails.driver_details.number_plate }}</div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </Transition>
          </div>
        </div>

      </div>

      <!-- Pagination — shown only when there's more than one page -->
      <div class="oh-pager" v-if="!loading && totalPages > 1">
        <div class="oh-pager-info">
          Showing <strong>{{ rangeStart }}–{{ rangeEnd }}</strong> of <strong>{{ tableData.data.length }}</strong>
        </div>
        <div class="oh-pager-controls">
          <button
            class="oh-pager-btn"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
            aria-label="Previous page"
          >
            ‹
          </button>
          <template v-for="(p, idx) in pageButtons" :key="idx">
            <span v-if="p === '…'" class="oh-pager-ellipsis">…</span>
            <button
              v-else
              class="oh-pager-btn"
              :class="{ 'oh-pager-btn--active': p === currentPage }"
              @click="goToPage(p as number)"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="oh-pager-btn"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <div class="oh-empty" v-if="!loading && tableData.data.length === 0">
        <div class="oh-empty-icon">📦</div>
        <div class="oh-empty-text">No orders yet</div>
        <button class="oh-btn oh-btn--navy" @click="router.push('/request')">Place your first order</button>
      </div>

    </div>

    <!-- Document Modal -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="docModal.open" class="dm-backdrop" @click.self="closeDocModal">
          <div class="dm-modal">

            <!-- Modal header -->
            <div class="dm-header">
              <div class="dm-header-left">
                <div class="dm-header-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <div>
                  <div class="dm-title">Delivery Documents</div>
                  <div class="dm-subtitle">{{ docModal.orderNumber }}</div>
                </div>
              </div>
              <button class="dm-close" @click="closeDocModal">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <!-- Main viewer -->
            <div class="dm-body">

              <!-- Left: viewer -->
              <div class="dm-viewer">
                <div class="dm-viewer-main">
                  <!-- Image viewer -->
                  <img
                    v-if="docModal.docs[docModal.active] && isImage(docModal.docs[docModal.active].src)"
                    :src="docModal.docs[docModal.active].src"
                    :alt="docModal.docs[docModal.active].title"
                    class="dm-viewer-img"
                  />
                  <!-- PDF / doc viewer -->
                  <iframe
                    v-else-if="docModal.docs[docModal.active]"
                    :src="docModal.docs[docModal.active].src"
                    class="dm-viewer-iframe"
                    frameborder="0"
                  ></iframe>
                </div>
                <!-- Nav arrows -->
                <button v-if="docModal.docs.length > 1" class="dm-nav dm-nav--prev" @click="docModal.active = (docModal.active - 1 + docModal.docs.length) % docModal.docs.length" :disabled="docModal.docs.length <= 1">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <button v-if="docModal.docs.length > 1" class="dm-nav dm-nav--next" @click="docModal.active = (docModal.active + 1) % docModal.docs.length" :disabled="docModal.docs.length <= 1">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
                <!-- Viewer footer -->
                <div class="dm-viewer-footer">
                  <span class="dm-doc-name">{{ docModal.docs[docModal.active]?.title || 'Document' }}</span>
                  <a :href="docModal.docs[docModal.active]?.src" target="_blank" class="dm-open-btn">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    Open original
                  </a>
                </div>
              </div>

              <!-- Right: sidebar -->
              <div class="dm-sidebar">
                <!-- Thumbnail strip -->
                <div class="dm-thumbs-label">{{ docModal.docs.length }} document{{ docModal.docs.length !== 1 ? 's' : '' }}</div>
                <div class="dm-thumbs">
                  <div
                    v-for="(doc, i) in docModal.docs"
                    :key="i"
                    class="dm-thumb"
                    :class="{ 'dm-thumb--active': docModal.active === i }"
                    @click="docModal.active = i"
                  >
                    <img v-if="isImage(doc.src)" :src="doc.src" :alt="doc.title" class="dm-thumb-img" />
                    <div v-else class="dm-thumb-file">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#475569" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <span>{{ getExt(doc.src) }}</span>
                    </div>
                    <div class="dm-thumb-title">{{ doc.title || 'Doc ' + (i+1) }}</div>
                  </div>
                </div>

                <!-- Download section -->
                <div class="dm-upload-section">
                  <div class="dm-upload-label">Downloads</div>
                  <div class="dm-download-list">
                    <a
                      v-for="(doc, di) in docModal.docs"
                      :key="di"
                      :href="doc.src"
                      :download="doc.title || 'document-' + (di + 1)"
                      class="dm-download-item"
                      @click.stop
                    >
                      <div class="dm-download-icon">
                        <svg v-if="isImage(doc.src)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      </div>
                      <div class="dm-download-info">
                        <span class="dm-download-name">{{ doc.title || 'Document ' + (di + 1) }}</span>
                        <span class="dm-download-type">{{ getExt(doc.src) }}</span>
                      </div>
                      <div class="dm-download-arrow">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="8 17 12 21 16 17"/><line x1="12" y1="3" x2="12" y2="21"/></svg>
                      </div>
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, watch } from "vue";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";
import { DateTime } from "luxon";
import { unparse } from "papaparse";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const loading = ref(false);
const tableKey = ref("id");
const tableData = reactive({ data: <any>[] });
const expandedRows = reactive(new Set<any>());

// Client-side pagination — 10 rows per page. CSV export still grabs the full set.
const PAGE_SIZE = 10;
const currentPage = ref(1);
const totalPages = computed(() => Math.max(1, Math.ceil(tableData.data.length / PAGE_SIZE)));
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return tableData.data.slice(start, start + PAGE_SIZE);
});
// If data shrinks (or never grows past 1 page) keep currentPage valid.
watch(totalPages, (n) => { if (currentPage.value > n) currentPage.value = n; });

const goToPage = (n: number) => {
  if (n < 1 || n > totalPages.value) return;
  currentPage.value = n;
  // Scroll the table back to the top so the user sees row 1 of the new page.
  if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
};

// Build a compact list of page buttons with ellipses for long page counts.
// e.g. for current=7 of 20: [1, '…', 6, 7, 8, '…', 20]
const pageButtons = computed<(number | "…")[]>(() => {
  const total = totalPages.value;
  const cur = currentPage.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  const left = Math.max(2, cur - 1);
  const right = Math.min(total - 1, cur + 1);
  if (left > 2) out.push("…");
  for (let i = left; i <= right; i++) out.push(i);
  if (right < total - 1) out.push("…");
  out.push(total);
  return out;
});

const rangeStart = computed(() =>
  tableData.data.length === 0 ? 0 : (currentPage.value - 1) * PAGE_SIZE + 1,
);
const rangeEnd = computed(() =>
  Math.min(currentPage.value * PAGE_SIZE, tableData.data.length),
);
const openDocs = reactive(new Set<any>());
const toggleDocs = (id: any) => {
  if (openDocs.has(id)) openDocs.delete(id);
  else openDocs.add(id);
};

const toggleRow = (row: any) => {
  if (expandedRows.has(row.id)) {
    expandedRows.delete(row.id);
  } else {
    expandedRows.add(row.id);
    if (!row.orderDetails) loadOrderDetails(row);
  }
};

const loadOrderDetails = async (row: any) => {
  try {
    const r = await axios.get(`${import.meta.env.VITE_ORDERS_SERVICE}orders?order_number=${row.order_number}&token=${store.getToken}`);
    if (r.status == 200 && r.data.status) {
      const idx = tableData.data.findIndex((el: any) => el.id == row.id);
      tableData.data[idx].orderDetails = r.data.data;
    }
  } catch { ElMessage({ message: "Error loading order details", type: "error" }); }
};

// Date-range filter state (re-added from main).
const dateRange = ref<string[] | undefined>(undefined);

const postData = async (
  startDate: string | null = null,
  endDate: string | null = null,
) => {
  loading.value = true;
  tableData.data = [];
  try {
    let url = `${import.meta.env.VITE_ORDERS_SERVICE}orders?company_id=${store.getUserData.company_id}&user_id=${store.getUserData.user_id}&order_status=*&token=${store.getToken}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    const r = await axios.get(url);
    if (r.status == 200) r.data.data.forEach((el: any) => tableData.data.push(el));
    else ElMessage({ message: "Failed to load orders.", type: "error" });
  } catch { ElMessage({ message: "Failed to load order history.", type: "error" }); }
  loading.value = false;
};
postData();

// Date filters are applied explicitly via the Apply button.
const handleDateRangeChange = () => {};

const applyFilters = () => {
  if (dateRange.value && Array.isArray(dateRange.value) && dateRange.value.length === 2) {
    postData(dateRange.value[0] as string, dateRange.value[1] as string);
  } else {
    postData();
  }
};

const clearFilters = () => {
  dateRange.value = undefined;
  postData();
};

const transformDate = (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy, HH:mm:ss");
const transformDateSQL = (date: string) => DateTime.fromISO(date, { zone: "UTC" }).toFormat("dd/MM/yyyy, HH:mm:ss");

const getStatusClass = (status: number) => {
  if (status === 1) return 'oh-badge--pending';
  if (status >= 2 && status < 5) return 'oh-badge--active';
  if (status >= 5) return 'oh-badge--delivered';
  return 'oh-badge--default';
};

// Internal nav link — opens the authenticated TrackOrder page. Prefer the
// unguessable tracking_token; fall back to order_number for legacy rows.
const returnOrderTrackingLink = (row: { tracking_token?: string | null; order_number: string }) =>
  row.tracking_token ? `/track?token=${row.tracking_token}` : `/track?order=${row.order_number}`;

// Public shareable link for the recipient — opens the unauthenticated /trip
// page with the stripped payload. Only meaningful when tracking_token exists.
const returnPublicTripLink = (row: { tracking_token?: string | null }) =>
  row.tracking_token
    ? `${window.location.origin}/trip?token=${row.tracking_token}`
    : '';
const hasDeliveryDocuments = (path: any) => path.some((obj: any) => obj.hasOwnProperty("document_info"));
const extractDocumentsInfo = (data: any) => data.flatMap((item: any) => item.document_info ? item.document_info.map((doc: any) => ({ src: `${import.meta.env.VITE_SPACES_OBJECT_STORAGE}${doc.location}`, title: doc.document_type })) : []);

const isImage = (src: string) => /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(src);
const getExt = (src: string) => { const m = src?.match(/\.([a-z0-9]+)$/i); return m ? m[1].toUpperCase() : 'FILE'; };

// Doc modal
const docModal = reactive({ open: false, docs: <any>[], active: 0, orderNumber: '' });
const openDocModal = (docs: any[], index: number, row: any) => {
  docModal.docs = docs; docModal.active = index; docModal.orderNumber = row.order_number; docModal.open = true;
};
const closeDocModal = () => { docModal.open = false; };

// Upload

const downloadCSV = () => {
  if (!tableData.data.length) { ElMessage({ message: "No data to download", type: "warning" }); return; }
  const csv = unparse(tableData.data.map((row: any) => ({ Transaction: row.order_number, Status: row.order_status_text, User: row.user_name, Date: transformDateSQL(row.scheduled_time), Amount: row.cost, From: row.pickup, To: row.dropoff })));
  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  link.download = "orders.csv";
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
* { box-sizing: border-box; }

.oh-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.oh-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }

/* ── Header ─────────────────────────────── */
.oh-header { background: white; padding: 24px 0 0; }
.oh-header-inner {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 18px;
}
.oh-title { font-size: 1.3rem; font-weight: 700; color: #12284A; margin: 0; letter-spacing: -0.01em; }
.oh-header-actions { display: flex; gap: 8px; }
.oh-btn {
  height: 38px; padding: 0 16px; border-radius: 8px; font-family: 'DM Sans', sans-serif;
  font-size: 0.82rem; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s; white-space: nowrap;
}
.oh-btn--ghost { background: white; color: #12284A; border: 1.5px solid #e2e8f0; }
.oh-btn--ghost:hover { border-color: #12284A; }
.oh-btn--navy { background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%); color: white; }
.oh-btn--navy:hover { opacity: 0.92; }
.oh-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Filters + summary ─────────────────── */
.oh-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 16px;
}
.oh-filter { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

/* ── List wrapper ──────────────────────── */
.oh-list-wrap { padding-top: 4px; padding-bottom: 48px; }

/* ── Table ─────────────────────────────── */
.oh-table {
  width: 100%;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Header row */
.oh-thead {
  display: grid;
  grid-template-columns: 140px 120px 1fr 150px 120px 1fr 1fr 90px;
  background: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 0 20px;
}
.oh-th {
  font-size: 0.68rem; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.08em;
  padding: 12px 10px; white-space: nowrap;
}
.oh-th--amount { text-align: right; }
.oh-th--action { text-align: right; }

/* Body */
.oh-tbody { display: flex; flex-direction: column; }

/* Row wrap */
.oh-row-wrap { border-bottom: 1px solid #f1f5f9; }
.oh-row-wrap:last-child { border-bottom: none; }
.oh-row-wrap--open { background: white; }

/* Data row */
.oh-row {
  display: grid;
  grid-template-columns: 140px 120px 1fr 150px 120px 1fr 1fr 90px;
  align-items: center;
  padding: 0 20px;
  height: 56px;
  cursor: pointer;
  transition: background 0.12s;
}
.oh-row:hover { background: #f8fafc; }
.oh-row-wrap--open .oh-row { background: #f8fafc; }

/* Cells */
.oh-td { padding: 0 10px; overflow: hidden; }

.oh-ref {
  font-size: 0.84rem; font-weight: 700; color: #12284A;
  letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap;
}

.oh-td-text {
  font-size: 0.83rem; color: #334155; font-weight: 500;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block;
  text-transform: capitalize;
}

.oh-td-muted {
  font-size: 0.81rem; color: #64748b; font-weight: 400;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block;
}

.oh-td-amount {
  font-size: 0.84rem; color: #12284A; font-weight: 700;
  text-align: right; display: block; white-space: nowrap;
}

/* Status badge — pill outline style matching the reference */
.oh-badge {
  display: inline-flex; align-items: center;
  padding: 4px 12px; border-radius: 20px;
  font-size: 0.74rem; font-weight: 600; white-space: nowrap;
  border: 1.5px solid currentColor;
}
.oh-badge--pending   { color: #dc2626; background: #fef2f2; border-color: #fca5a5; }
.oh-badge--active    { color: #2563eb; background: #eff6ff; border-color: #93c5fd; }
.oh-badge--delivered { color: #16a34a; background: #f0fdf4; border-color: #86efac; }
.oh-badge--default   { color: #64748b; background: #f8fafc; border-color: #cbd5e1; }

/* View link */
.oh-view-link {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.8rem; font-weight: 600; color: #2563eb;
  float: right; white-space: nowrap; transition: gap 0.15s;
}
.oh-row:hover .oh-view-link { gap: 7px; }

/* ── Expanded detail ────────────────────── */
.oh-detail {
  border-top: 1.5px solid #e2e8f0;
  background: #f0f4fa;
  padding: 14px;
}
.oh-detail-grid { display: grid; grid-template-columns: 1fr 1.3fr 0.85fr; gap: 10px; }

.oh-dcard {
  background: white; border-radius: 10px;
  border: 1.5px solid #e2e8f0; padding: 14px 16px;
}
.oh-dcard-head { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
.oh-dcard-icon {
  width: 28px; height: 28px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.oh-dcard-icon--blue   { background: #eff6ff; }
.oh-dcard-icon--purple { background: #f5f3ff; }
.oh-dcard-icon--teal   { background: #f0fdfa; }
.oh-dcard-label { font-size: 0.72rem; font-weight: 700; color: #334155; text-transform: uppercase; letter-spacing: 0.07em; }

.oh-dkv-list { display: flex; flex-direction: column; gap: 0; }
.oh-dkv {
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  padding: 8px 0; border-bottom: 1px solid #f1f5f9;
}
.oh-dkv:last-child { border-bottom: none; padding-bottom: 0; }
.oh-dk { font-size: 0.78rem; color: #64748b; font-weight: 500; flex-shrink: 0; }
.oh-dv { font-size: 0.83rem; color: #0f172a; font-weight: 600; text-align: right; }
.oh-dv--navy { color: #12284A; font-weight: 700; }
.oh-dv--green { color: #15803d; font-weight: 600; }

/* Route */
.oh-stop { display: flex; gap: 10px; }
.oh-stop-track { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; padding-top: 4px; }
.oh-stop-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.oh-stop-dot--green { background: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
.oh-stop-dot--amber { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.15); }
.oh-stop-dot--mid { background: #94a3b8; }
.oh-stop-line { width: 2px; flex: 1; min-height: 14px; background: #e2e8f0; margin: 3px 0; }
.oh-stop-text { flex: 1; padding-bottom: 10px; }
.oh-stop-type {
  font-size: 0.67rem; color: #94a3b8; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  display: flex; align-items: center; gap: 5px;
}
.oh-stop-type--drop { color: #64748b; }
.oh-stop-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 15px; height: 15px; border-radius: 50%;
  background: #f1f5f9; color: #475569;
  font-size: 0.62rem; font-weight: 700; flex-shrink: 0;
}
.oh-stop-name { font-size: 0.81rem; color: #1e293b; font-weight: 500; margin-top: 3px; line-height: 1.35; }
.oh-detail-actions { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.oh-track-link { font-size: 0.81rem; font-weight: 600; color: #2563eb; text-decoration: none; }
.oh-track-link:hover { color: #1d4ed8; }
.oh-docs-section { margin-top: 12px; border-top: 1px solid #f1f5f9; padding-top: 10px; }

.oh-docs-toggle {
  display: flex; align-items: center; gap: 6px;
  background: none; border: none; padding: 0; cursor: pointer;
  font-family: 'DM Sans', sans-serif; font-size: 0.8rem; font-weight: 600;
  color: #475569; transition: color 0.15s; width: 100%;
}
.oh-docs-toggle:hover { color: #12284A; }
.oh-docs-count {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%;
  background: #e2e8f0; color: #475569;
  font-size: 0.65rem; font-weight: 700;
}
.oh-docs-chevron { margin-left: auto; color: #94a3b8; transition: transform 0.2s; }
.oh-docs-chevron--open { transform: rotate(180deg); }

.oh-docs-gallery {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px; margin-top: 10px;
}
.oh-doc-thumb {
  text-decoration: none; display: flex; flex-direction: column; gap: 5px;
  cursor: pointer;
}
.oh-doc-img-wrap {
  position: relative; border-radius: 8px; overflow: hidden;
  aspect-ratio: 1; background: #f1f5f9;
  border: 1.5px solid #e2e8f0; transition: border-color 0.15s;
}
.oh-doc-thumb:hover .oh-doc-img-wrap { border-color: #2563eb; }
.oh-doc-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.oh-doc-overlay {
  position: absolute; inset: 0; background: rgba(18,40,74,0.55);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity 0.18s;
}
.oh-doc-thumb:hover .oh-doc-overlay { opacity: 1; }
.oh-doc-label {
  font-size: 0.68rem; color: #64748b; font-weight: 500;
  text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* docs expand transition */
.docs-expand-enter-active, .docs-expand-leave-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); overflow: hidden; }
.docs-expand-enter-from, .docs-expand-leave-to { opacity: 0; max-height: 0; }
.docs-expand-enter-to, .docs-expand-leave-from { opacity: 1; max-height: 400px; }

/* Driver */
.oh-driver { display: flex; align-items: center; gap: 12px; }
.oh-driver-avatar {
  width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 1rem; font-weight: 700;
}
.oh-driver-name { font-size: 0.86rem; font-weight: 600; color: #12284A; }
.oh-driver-plate {
  display: inline-block; background: #f1f5f9; border-radius: 4px;
  padding: 2px 8px; font-size: 0.71rem; font-weight: 600;
  color: #475569; letter-spacing: 0.06em; margin-top: 4px;
}

/* Transition */
.expand-enter-active, .expand-leave-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); overflow: hidden; }
.expand-enter-from, .expand-leave-to { opacity: 0; max-height: 0; }
.expand-enter-to, .expand-leave-from { opacity: 1; max-height: 500px; }

/* Empty */
.oh-empty { text-align: center; padding: 60px 0; display: flex; flex-direction: column; align-items: center; gap: 12px; }
.oh-empty-icon { font-size: 2.5rem; }
.oh-empty-text { font-size: 1rem; font-weight: 500; color: #94a3b8; }

/* ── Document Modal ──────────────────────── */
.dm-backdrop {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(10, 20, 40, 0.65);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.dm-modal {
  background: white; border-radius: 16px;
  width: 100%; max-width: 960px; max-height: 90vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.3);
  overflow: hidden;
}
.dm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1.5px solid #f1f5f9; flex-shrink: 0;
}
.dm-header-left { display: flex; align-items: center; gap: 10px; }
.dm-header-icon {
  width: 34px; height: 34px; border-radius: 9px; background: #eff6ff;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.dm-title { font-size: 0.95rem; font-weight: 700; color: #12284A; }
.dm-subtitle { font-size: 0.75rem; color: #94a3b8; font-weight: 500; margin-top: 1px; letter-spacing: 0.04em; text-transform: uppercase; }
.dm-close {
  width: 32px; height: 32px; border-radius: 8px; border: none;
  background: #f1f5f9; color: #64748b; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s; flex-shrink: 0;
}
.dm-close:hover { background: #fee2e2; color: #dc2626; }

.dm-body { display: flex; flex: 1; overflow: hidden; min-height: 0; }

/* Viewer */
.dm-viewer {
  flex: 1; position: relative; background: #0f172a;
  display: flex; flex-direction: column; min-width: 0;
}
.dm-viewer-main {
  flex: 1; display: flex; align-items: center; justify-content: center;
  overflow: hidden; padding: 20px;
}
.dm-viewer-img {
  max-width: 100%; max-height: 100%; object-fit: contain;
  border-radius: 6px;
}
.dm-viewer-iframe { width: 100%; height: 100%; border: none; border-radius: 6px; background: white; }
.dm-viewer-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; background: rgba(0,0,0,0.3); flex-shrink: 0;
}
.dm-doc-name { font-size: 0.8rem; color: rgba(255,255,255,0.8); font-weight: 500; }
.dm-open-btn {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.75rem; font-weight: 600; color: rgba(255,255,255,0.7);
  text-decoration: none; transition: color 0.15s;
}
.dm-open-btn:hover { color: white; }

/* Nav arrows */
.dm-nav {
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background: rgba(255,255,255,0.15); color: white; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s; backdrop-filter: blur(4px);
}
.dm-nav:hover { background: rgba(255,255,255,0.3); }
.dm-nav--prev { left: 12px; }
.dm-nav--next { right: 12px; }

/* Sidebar */
.dm-sidebar {
  width: 220px; flex-shrink: 0; border-left: 1.5px solid #f1f5f9;
  display: flex; flex-direction: column; overflow: hidden;
}

.dm-thumbs-label {
  font-size: 0.68rem; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.07em;
  padding: 12px 14px 8px;
}
.dm-thumbs {
  display: flex; flex-direction: column; gap: 4px;
  padding: 0 10px; overflow-y: auto; flex-shrink: 0; max-height: 240px;
}
.dm-thumb {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 8px; border-radius: 8px; cursor: pointer;
  transition: background 0.12s; border: 1.5px solid transparent;
}
.dm-thumb:hover { background: #f8fafc; }
.dm-thumb--active { background: #eff6ff; border-color: #93c5fd; }
.dm-thumb-img { width: 36px; height: 36px; border-radius: 6px; object-fit: cover; flex-shrink: 0; border: 1px solid #e2e8f0; }
.dm-thumb-file {
  width: 36px; height: 36px; border-radius: 6px; flex-shrink: 0;
  background: #f1f5f9; border: 1px solid #e2e8f0;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
}
.dm-thumb-file span { font-size: 0.55rem; font-weight: 700; color: #64748b; letter-spacing: 0.02em; }
.dm-thumb-title { font-size: 0.75rem; color: #334155; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }

/* Downloads */
.dm-upload-section {
  padding: 12px 10px 16px; border-top: 1.5px solid #f1f5f9; flex: 1; overflow-y: auto;
}
.dm-upload-label {
  font-size: 0.68rem; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 8px; padding: 0 4px;
}
.dm-download-list { display: flex; flex-direction: column; gap: 4px; }
.dm-download-item {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 10px; border-radius: 9px;
  border: 1.5px solid #e2e8f0; background: #f8fafc;
  text-decoration: none; transition: all 0.15s; cursor: pointer;
}
.dm-download-item:hover { border-color: #12284A; background: #eff6ff; }
.dm-download-item:hover .dm-download-arrow { color: #12284A; transform: translateY(2px); }
.dm-download-icon {
  width: 30px; height: 30px; border-radius: 7px; background: white;
  border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; color: #475569;
}
.dm-download-info { flex: 1; min-width: 0; }
.dm-download-name { display: block; font-size: 0.78rem; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dm-download-type { font-size: 0.67rem; color: #94a3b8; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
.dm-download-arrow { color: #94a3b8; transition: all 0.2s; flex-shrink: 0; }

/* Modal transitions */
.modal-fade-enter-active { transition: all 0.22s cubic-bezier(0.4,0,0.2,1); }
.modal-fade-leave-active { transition: all 0.18s cubic-bezier(0.4,0,0.2,1); }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-from .dm-modal { transform: scale(0.96) translateY(8px); }
.modal-fade-enter-to .dm-modal { transform: scale(1) translateY(0); }

/* ── Pagination ─────────────────────────── */
.oh-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 18px 12px 4px;
  font-family: 'DM Sans', sans-serif;
}
.oh-pager-info {
  font-size: 0.85rem;
  color: #6b7280;
}
.oh-pager-info strong {
  color: #12284A;
  font-weight: 600;
}
.oh-pager-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}
.oh-pager-btn {
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  background: white;
  color: #12284A;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  font-family: inherit;
}
.oh-pager-btn:hover:not(:disabled):not(.oh-pager-btn--active) {
  border-color: #12284A;
}
.oh-pager-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.oh-pager-btn--active {
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  border-color: transparent;
  color: white;
}
.oh-pager-ellipsis {
  padding: 0 6px;
  color: #94a3b8;
  font-size: 0.875rem;
  user-select: none;
}
@media (max-width: 640px) {
  .oh-pager {
    justify-content: center;
  }
  .oh-pager-info {
    width: 100%;
    text-align: center;
  }
}
</style>